/** How many recent workspace ids the recent-workspaces cache keeps. */
export const MAX_STORED_RECENT_WORKSPACES = 5;

/** `recentIds` with `workspaceId` moved to the front, capped for the cache. */
export const withRecentWorkspaceId = ({
    recentIds,
    workspaceId,
}: {
    recentIds: readonly string[];
    workspaceId: string;
}): string[] =>
    [workspaceId, ...recentIds.filter((id) => id !== workspaceId)].slice(
        0,
        MAX_STORED_RECENT_WORKSPACES,
    );

/**
 * The workspaces to list as recent: the ones in `recentIds` (most recent
 * first, unknown ids skipped), topped up with the rest of `workspaces` in
 * list order, up to `limit`.
 */
export const pickRecentWorkspaces = <Workspace extends { id: string }>({
    workspaces,
    recentIds,
    limit,
}: {
    workspaces: readonly Workspace[];
    recentIds: readonly string[];
    limit: number;
}): Workspace[] => {
    const workspaceById = new Map(
        workspaces.map((workspace) => [workspace.id, workspace]),
    );
    const recentWorkspaces = [...new Set(recentIds)].flatMap((id) => {
        const workspace = workspaceById.get(id);
        return workspace === undefined ? [] : [workspace];
    });
    const pickedIds = new Set(recentWorkspaces.map(({ id }) => id));
    const otherWorkspaces = workspaces.filter(({ id }) => !pickedIds.has(id));

    return [...recentWorkspaces, ...otherWorkspaces].slice(0, limit);
};
